import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyGetAllCustomersQuery } from 'containers/invoices/api';
import { useLazyGetMerchantsByTeamIdReceiptsQuery } from 'containers/receipts/api';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import { useAppSelector } from 'hooks';
import { JobLeadSourceTypes, JobStatus, NO_TAGS_FILTER_ID } from '../constant';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import {
  calculateDateRangeLabel,
  getDateDefaultRangeForMaps,
  getDateRangeFromSchedulePreset,
  getSchedulePresetFromLabel,
  getTextForFilter,
  prepareEndDateForSave,
  prepareStartDateForSave
} from 'containers/dispatch/utils';
import { useLazyGetExternalCompaniesQuery } from 'containers/settings/external-company-settings/api';
import { CommissionStatus } from 'containers/settings/commission-settings/constants';
import { useLazyGetTagsQuery } from 'containers/tags/api';
import { selectCurrentTeamJobPermissions, selectMerchantCustomizations } from 'containers/app/appSlice';
import { JobStatusFilters } from '../utils';
import { AnytimeCalendarIcon, NotScheduledCalendarIcon } from 'assets/icons';
import { useLazyGetServiceAreasQuery } from 'containers/settings/service-areas/api';
import { useViewFilters } from '../hooks/useViewFilters';
import { SchedulePreset } from 'containers/dispatch/constants';
import { format } from 'date-fns';
import { InvoiceStatusesForFilter } from 'containers/dispatch/constants';
import { EstimateStatusesForFilter } from 'containers/estimates/constants';
import MoreFilters, { AppliedFilter, FilterOption } from 'components/more-filters/MoreFilters';
import { JOB_FILTER_OPTIONS, JobFilterIds } from '../constant';

interface JobsFilterProps {
  statuses?: string[];
  setStatuses?: (value: JobStatus[]) => void;
  currentTeamId: number;
  selectedCustomers: string[];
  setSelectedCustomers: (value: string[]) => void;
  selectedMerchants: string[];
  setSelectedMerchants: (value: string[]) => void;
  selectedTeamMembers?: string[];
  setSelectedTeamMembers?: (value: string[]) => void;
  leadSources: string[];
  setLeadSources: (value: string[]) => void;
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  scheduleStartDate: string | null;
  setScheduleStartDate: (startDate: string | null) => void;
  scheduleEndDate: string | null;
  setScheduleEndDate: (endDate: string | null) => void;
  createdStartDate?: string | null;
  setCreatedStartDate?: (startDate: string | null) => void;
  createdEndDate?: string | null;
  setCreatedEndDate?: (endDate: string | null) => void;
  externalCompanies: string[];
  setExternalCompanies: (value: string[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  showStatusFilter: boolean;
  showTeamFilter: boolean;
  selectedServiceAreas: string[];
  setSelectedServiceAreas: (value: string[]) => void;
  onDateRangeChange?: (startDate: string | null, endDate: string | null) => void;
  selectedInvoiceStatus?: string[];
  setSelectedInvoiceStatus?: (value: string[]) => void;
  selectedEstimateStatus?: string[];
  setSelectedEstimateStatus?: (value: string[]) => void;
  moreFilterOptions?: FilterOption[];
}

const JobsFilter: FC<JobsFilterProps> = ({
  statuses,
  setStatuses,
  currentTeamId,
  selectedCustomers,
  setSelectedCustomers,
  selectedMerchants,
  setSelectedMerchants,
  selectedTeamMembers,
  setSelectedTeamMembers,
  leadSources,
  setLeadSources,
  amountFilter,
  setAmountFilter,
  scheduleStartDate,
  setScheduleStartDate,
  scheduleEndDate,
  setScheduleEndDate,
  createdStartDate,
  setCreatedStartDate,
  createdEndDate,
  setCreatedEndDate,
  externalCompanies,
  setExternalCompanies,
  selectedTags,
  setSelectedTags,
  showStatusFilter = true,
  showTeamFilter = true,
  selectedServiceAreas,
  setSelectedServiceAreas,
  onDateRangeChange,
  selectedInvoiceStatus,
  setSelectedInvoiceStatus,
  selectedEstimateStatus,
  setSelectedEstimateStatus,
  moreFilterOptions = JOB_FILTER_OPTIONS
}) => {
  // API queries
  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdReceiptsQuery();
  const [getExternalCompaniesQuery, getExternalCompaniesState] = useLazyGetExternalCompaniesQuery();
  const [getTagsQuery, getTagsQueryState] = useLazyGetTagsQuery();
  const [getServiceAreasQuery, { data: serviceAreasData }] = useLazyGetServiceAreasQuery();

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;
  const { data: companiesData } = getExternalCompaniesState;
  const { data: tagsData } = getTagsQueryState;

  // Draft states for dropdowns
  const [statusesDraft, setStatusesDraft] = useState<string[]>([]);
  const [tagsSearchString, setTagsSearchString] = useState('');
  const [selectedTagsDraft, setSelectedTagsDraft] = useState<string[]>([]);
  const [assigneesDraft, setAssigneesDraft] = useState<string[]>([]);
  const [assigneeSearchString, setAssigneeSearchString] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

  const { updateCurrentViewFilters, currentViewFilters } = useViewFilters();
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations);

  const statusFilterList = JobStatusFilters(jobPermissions, teamConfigs);
  const { viewJobSource } = useAppSelector(selectCurrentTeamJobPermissions);

  // Memoized data options
  const customerOptions = useMemo(() => getCustomersResponse?.customers || [], [getCustomersResponse?.customers]);
  const merchantOptions = useMemo(() => getMerchantsResponse || [], [getMerchantsResponse]);
  const tagOptions = useMemo(() => tagsData?.data?.tags || [], [tagsData?.data?.tags]);
  const serviceAreaOptions = useMemo(() => serviceAreasData?.data || [], [serviceAreasData?.data]);

  const externalCompanyOptions = useMemo(
    () =>
      companiesData?.data?.map(company => ({
        id: company.id,
        value: company.id,
        label: company.companyName
      })) || [],
    [companiesData?.data]
  );

  // Memoized filter options with conditional filtering
  const availableFilterOptions = useMemo(() => {
    let filterOptions = [...moreFilterOptions];

    // Filter based on permissions
    if (!viewJobSource) {
      filterOptions = filterOptions.filter(
        filter => filter.id !== JobFilterIds.LEAD_SOURCE && filter.id !== JobFilterIds.EXTERNAL_COMPANY
      );
    }

    // Filter based on data availability
    if (!serviceAreaOptions?.length) {
      filterOptions = filterOptions.filter(filter => filter.id !== JobFilterIds.SERVICE_AREA);
    }

    if (!externalCompanyOptions?.length) {
      filterOptions = filterOptions.filter(filter => filter.id !== JobFilterIds.EXTERNAL_COMPANY);
    }

    return filterOptions;
  }, [viewJobSource, serviceAreaOptions, externalCompanyOptions]);

  // Get filter data function
  const getFilterData = useCallback(
    (filterId: string) => {
      switch (filterId) {
        case JobFilterIds.CUSTOMER:
          return customerOptions;
        case JobFilterIds.CREATED_BY:
          return merchantOptions;
        case JobFilterIds.LEAD_SOURCE:
          return JobLeadSourceTypes;
        case JobFilterIds.EXTERNAL_COMPANY:
          return externalCompanyOptions;
        case JobFilterIds.SERVICE_AREA:
          return serviceAreaOptions;
        case JobFilterIds.INVOICE_STATUS:
          return InvoiceStatusesForFilter;
        case JobFilterIds.ESTIMATE_STATUS:
          return EstimateStatusesForFilter;
        default:
          return [];
      }
    },
    [customerOptions, merchantOptions, externalCompanyOptions, serviceAreaOptions]
  );

  const tagFilterOptions = useMemo(() => {
    return [{ id: NO_TAGS_FILTER_ID, name: 'No tags' }, ...tagOptions];
  }, [tagOptions]);

  // Initialize API calls
  useEffect(() => {
    if (currentTeamId) {
      getCustomersQuery({ teamId: currentTeamId });
      getMerchantsQuery({ teamId: currentTeamId });
      getExternalCompaniesQuery({ teamId: currentTeamId.toString(), status: CommissionStatus.ACTIVE });
      getTagsQuery({ teamId: currentTeamId });
      getServiceAreasQuery({ teamId: currentTeamId.toString(), params: { sortBy: 'createdAt', sortOrder: 'ASC' } });
    }
  }, [currentTeamId]);

  // Sync draft states
  useEffect(() => {
    setAssigneesDraft(selectedTeamMembers || []);
  }, [selectedTeamMembers]);

  // Update applied filters when filter values change
  useEffect(() => {
    const appliedFiltersToSet: AppliedFilter[] = [];

    // Customer filter
    if (selectedCustomers.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.CUSTOMER);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: selectedCustomers,
          displayValue: getTextForFilter(customerOptions, selectedCustomers, 'ID', 'name', 'lastName')
        });
      }
    }

    // Created by filter
    if (selectedMerchants.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.CREATED_BY);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: selectedMerchants,
          displayValue: getTextForFilter(merchantOptions, selectedMerchants, 'merchantId', 'firstName')
        });
      }
    }

    // Lead source filter
    if (leadSources.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.LEAD_SOURCE);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: leadSources,
          displayValue: getTextForFilter(JobLeadSourceTypes, leadSources, 'value', 'label')
        });
      }
    }

    // Amount filter
    if (amountFilter.filterOperation !== AmountFilterOperation.ALL) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.AMOUNT);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: amountFilter,
          displayValue: '' // Will be handled by CustomAmountFilter
        });
      }
    }

    // External company filter
    if (externalCompanies.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.EXTERNAL_COMPANY);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: externalCompanies,
          displayValue: getTextForFilter(externalCompanyOptions, externalCompanies, 'value', 'label')
        });
      }
    }

    // Service area filter
    if (selectedServiceAreas.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.SERVICE_AREA);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: selectedServiceAreas,
          displayValue: getTextForFilter(serviceAreaOptions, selectedServiceAreas, 'id', 'name')
        });
      }
    }

    // Invoice status filter
    if (selectedInvoiceStatus && selectedInvoiceStatus.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.INVOICE_STATUS);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: selectedInvoiceStatus,
          displayValue: getTextForFilter(InvoiceStatusesForFilter, selectedInvoiceStatus, 'value', 'label')
        });
      }
    }

    // Estimate status filter
    if (selectedEstimateStatus && selectedEstimateStatus.length > 0) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.ESTIMATE_STATUS);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: selectedEstimateStatus,
          displayValue: getTextForFilter(EstimateStatusesForFilter, selectedEstimateStatus, 'value', 'label')
        });
      }
    }

    // Created on filter
    if (createdStartDate && createdEndDate) {
      const filterOption = availableFilterOptions.find(filter => filter.id === JobFilterIds.CREATED_ON);
      if (filterOption) {
        appliedFiltersToSet.push({
          ...filterOption,
          value: { startDate: createdStartDate, endDate: createdEndDate },
          displayValue: `${createdStartDate} - ${createdEndDate}`
        });
      }
    }

    setAppliedFilters(appliedFiltersToSet);
  }, [
    selectedCustomers,
    selectedMerchants,
    leadSources,
    amountFilter,
    externalCompanies,
    selectedServiceAreas,
    selectedInvoiceStatus,
    selectedEstimateStatus,
    availableFilterOptions,
    customerOptions,
    merchantOptions,
    serviceAreaOptions,
    externalCompanyOptions,
    createdStartDate,
    createdEndDate
  ]);

  // Handler functions
  const handleStatusChange = (newStatuses: JobStatus[]) => {
    if (setStatuses) {
      setStatuses(newStatuses);
    }
    updateCurrentViewFilters({ ...currentViewFilters, status: newStatuses });
  };

  const handleTeamMembersChange = (newTeamMembers: string[]) => {
    if (setSelectedTeamMembers) {
      setSelectedTeamMembers(newTeamMembers);
    }
    updateCurrentViewFilters({ ...currentViewFilters, assignees: newTeamMembers });
  };

  const handleApplyFilterFromMoreFilters = useCallback(
    (filterData: { selectedFilterItems: string[] | AmountFilter | any; filter: FilterOption }) => {
      const { selectedFilterItems, filter } = filterData;

      const updatedFilters = { ...currentViewFilters };

      switch (filter.id) {
        case JobFilterIds.CUSTOMER:
          setSelectedCustomers(selectedFilterItems);
          updatedFilters.customerIds = selectedFilterItems;
          break;
        case JobFilterIds.CREATED_BY:
          setSelectedMerchants(selectedFilterItems);
          updatedFilters.createdBy = selectedFilterItems;
          break;
        case JobFilterIds.LEAD_SOURCE:
          setLeadSources(selectedFilterItems);
          updatedFilters.leadSource = selectedFilterItems;
          break;
        case JobFilterIds.AMOUNT:
          setAmountFilter(selectedFilterItems);
          updatedFilters.amount = selectedFilterItems;
          break;
        case JobFilterIds.EXTERNAL_COMPANY:
          setExternalCompanies(selectedFilterItems);
          updatedFilters.externalCompany = selectedFilterItems;
          break;
        case JobFilterIds.SERVICE_AREA:
          setSelectedServiceAreas(selectedFilterItems);
          updatedFilters.serviceArea = selectedFilterItems;
          break;
        case JobFilterIds.INVOICE_STATUS:
          if (setSelectedInvoiceStatus) {
            setSelectedInvoiceStatus(selectedFilterItems);
          }
          updatedFilters.invoiceStatus = selectedFilterItems;
          break;
        case JobFilterIds.ESTIMATE_STATUS:
          if (setSelectedEstimateStatus) {
            setSelectedEstimateStatus(selectedFilterItems);
          }
          updatedFilters.estimateStatus = selectedFilterItems;
          break;
        case JobFilterIds.CREATED_ON:
          setCreatedStartDate(selectedFilterItems.startDate);
          setCreatedEndDate(selectedFilterItems.endDate);
          updatedFilters.createdFrom = selectedFilterItems.startDate
            ? prepareStartDateForSave(selectedFilterItems.startDate)
            : null;
          updatedFilters.createdTo = selectedFilterItems.endDate
            ? prepareEndDateForSave(selectedFilterItems.endDate)
            : null;
          break;
      }
      updateCurrentViewFilters(updatedFilters);
    },
    [
      currentViewFilters,
      updateCurrentViewFilters,
      setSelectedCustomers,
      setSelectedMerchants,
      setLeadSources,
      setAmountFilter,
      setExternalCompanies,
      setSelectedTags,
      setSelectedServiceAreas,
      setSelectedInvoiceStatus,
      setSelectedEstimateStatus,
      setCreatedStartDate,
      setCreatedEndDate
    ]
  );
  const handleTagsChange = (newTags: string[]) => {
    setSelectedTags(newTags);
    const isNoTagsIncluded = newTags.includes(NO_TAGS_FILTER_ID);
    const tagsToSetForView = isNoTagsIncluded ? newTags.filter(tag => tag !== NO_TAGS_FILTER_ID) : newTags;
    updateCurrentViewFilters({ ...currentViewFilters, tags: tagsToSetForView, noTags: isNoTagsIncluded });
  };
  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const updatedFilters = { ...currentViewFilters };

      switch (filterId) {
        case JobFilterIds.CUSTOMER:
          setSelectedCustomers([]);
          updatedFilters.customerIds = [];
          break;
        case JobFilterIds.CREATED_BY:
          setSelectedMerchants([]);
          updatedFilters.createdBy = [];
          break;
        case JobFilterIds.LEAD_SOURCE:
          setLeadSources([]);
          updatedFilters.leadSource = [];
          break;
        case JobFilterIds.AMOUNT:
          setAmountFilter({ filterOperation: AmountFilterOperation.ALL, minAmount: null, maxAmount: null });
          updatedFilters.amount = { filterOperation: AmountFilterOperation.ALL, minAmount: null, maxAmount: null };
          break;
        case JobFilterIds.EXTERNAL_COMPANY:
          setExternalCompanies([]);
          updatedFilters.externalCompany = [];
          break;
        case JobFilterIds.SERVICE_AREA:
          setSelectedServiceAreas([]);
          updatedFilters.serviceArea = [];
          break;
        case JobFilterIds.INVOICE_STATUS:
          if (setSelectedInvoiceStatus) {
            setSelectedInvoiceStatus([]);
          }
          updatedFilters.invoiceStatus = [];
          break;
        case JobFilterIds.ESTIMATE_STATUS:
          if (setSelectedEstimateStatus) {
            setSelectedEstimateStatus([]);
          }
          updatedFilters.estimateStatus = [];
          break;
        case JobFilterIds.CREATED_ON:
          setCreatedStartDate(null);
          setCreatedEndDate(null);
          updatedFilters.createdFrom = null;
          updatedFilters.createdTo = null;
          break;
      }

      updateCurrentViewFilters(updatedFilters);
    },
    [
      currentViewFilters,
      updateCurrentViewFilters,
      setSelectedCustomers,
      setSelectedMerchants,
      setLeadSources,
      setAmountFilter,
      setExternalCompanies,
      setSelectedTags,
      setSelectedServiceAreas,
      setSelectedInvoiceStatus,
      setSelectedEstimateStatus,
      setCreatedStartDate,
      setCreatedEndDate
    ]
  );

  // Date range handlers
  const setDateRange = (updatedStartDate: string | null, updatedEndDate: string | null) => {
    if (onDateRangeChange) {
      onDateRangeChange(updatedStartDate, updatedEndDate);
    } else {
      if (!updatedStartDate && !updatedEndDate) {
        updateCurrentViewFilters({
          ...currentViewFilters,
          scheduledFrom: null,
          scheduledTo: null,
          isToday: false,
          schedulePreset: null,
          isUnscheduled: true
        });
      } else {
        // Check if this matches a schedule preset
        const dateRangeLabel = calculateDateRangeLabel(updatedStartDate, updatedEndDate);
        const preset = getSchedulePresetFromLabel(dateRangeLabel);

        // Special handling for "Today" - set isToday instead of schedulePreset
        if (dateRangeLabel === 'Today') {
          updateCurrentViewFilters({
            ...currentViewFilters,
            scheduledFrom: updatedStartDate,
            scheduledTo: updatedEndDate,
            isUnscheduled: false,
            schedulePreset: null,
            isToday: true
          });
        } else {
          updateCurrentViewFilters({
            ...currentViewFilters,
            scheduledFrom: updatedStartDate,
            scheduledTo: updatedEndDate,
            isToday: false,
            isUnscheduled: false,
            schedulePreset: preset
          });
        }
      }
    }
  };

  const handleStartDateChange = (date: string | null) => {
    setScheduleStartDate(date);
  };

  const handleEndDateChange = (date: string | null) => {
    setScheduleEndDate(date);
  };

  const handleClearDateRange = () => {
    updateCurrentViewFilters({
      ...currentViewFilters,
      scheduledFrom: null,
      scheduledTo: null,
      isToday: false,
      isUnscheduled: false,
      schedulePreset: null
    });
  };

  const getCustomDateRangeText = () => {
    // If schedule preset is selected, show the preset label
    if (currentViewFilters.schedulePreset) {
      const presetLabels = {
        [SchedulePreset.TODAY]: 'Today',
        [SchedulePreset.TOMORROW]: 'Tomorrow',
        [SchedulePreset.NEXT_7_DAYS]: 'Next 7 days',
        [SchedulePreset.NEXT_14_DAYS]: 'Next 14 days',
        [SchedulePreset.THIS_WEEK]: 'This Week',
        [SchedulePreset.LAST_WEEK]: 'Last Week',
        [SchedulePreset.THIS_MONTH]: 'This Month',
        [SchedulePreset.LAST_MONTH]: 'Last Month',
        [SchedulePreset.THIS_YEAR]: 'This year',
        [SchedulePreset.LAST_YEAR]: 'Last year'
      };
      return presetLabels[currentViewFilters.schedulePreset as SchedulePreset] || '';
    }

    if (currentViewFilters.isToday) {
      return 'Today';
    }

    const customText = '';
    if (scheduleStartDate && !scheduleEndDate) {
      return 'Any time';
    }

    if (currentViewFilters.isUnscheduled) return 'Unscheduled';
    return customText;
  };

  const unscheduledContent = (
    <div className="flex h-full w-full flex-col items-center justify-center text-center">
      <NotScheduledCalendarIcon className="mb-2 h-8 w-8 text-gray-500" />
      <span className="text-base font-medium">Not scheduled</span>
      <span className="text-sm text-primaryText">Jobs where schedule date and times are empty.</span>
    </div>
  );

  const anyTimeContent = (
    <div className="flex h-full w-full flex-col items-center justify-center text-center">
      <AnytimeCalendarIcon className="mb-2 h-8 w-8 text-gray-500" />
      <span className="text-base font-medium">Any time</span>
      <span className="text-sm text-primaryText">View all scheduled jobs</span>
    </div>
  );

  // Sync preset with actual dates
  useEffect(() => {
    if (currentViewFilters.schedulePreset) {
      const dateRange = getDateRangeFromSchedulePreset(currentViewFilters.schedulePreset as string);
      if (dateRange.startDate && dateRange.endDate) {
        const formattedStart = format(dateRange.startDate, 'yyyy-MM-dd');
        const formattedEnd = format(dateRange.endDate, 'yyyy-MM-dd');
        setScheduleStartDate(formattedStart);
        setScheduleEndDate(formattedEnd);
      }
    } else if (currentViewFilters.isToday) {
      const currentDate = format(new Date(), 'yyyy-MM-dd');
      setScheduleStartDate(currentDate);
      setScheduleEndDate(currentDate);
    }
  }, [currentViewFilters.schedulePreset, currentViewFilters.isToday, setScheduleStartDate, setScheduleEndDate]);

  return (
    <>
      <CustomDateRangePicker
        startDate={scheduleStartDate}
        setStartDate={handleStartDateChange}
        endDate={scheduleEndDate}
        setEndDate={handleEndDateChange}
        filterId="jobs-schedule-for-filter"
        allowFutureDate={true}
        customRanges={getDateDefaultRangeForMaps()}
        hasDefaultRanges={false}
        emptyStateContent={unscheduledContent}
        setDateRange={setDateRange}
        anytimeContent={anyTimeContent}
        placeholder="Scheduled for"
        customSecondaryText={getCustomDateRangeText()}
        onClear={handleClearDateRange}
      />
      {showStatusFilter && (
        <DropdownWithSearch
          filterId={JobFilterIds.JOB_STATUS}
          data={statusFilterList}
          selectedDraftArray={statusesDraft}
          setSelectedDraftArray={setStatusesDraft}
          selectedArray={statuses}
          setSelectedArray={handleStatusChange}
          dataKey="label"
          dataIdKey="value"
          buttonPrimaryText="Status"
          buttonSecondaryText={getTextForFilter(statusFilterList, statuses, 'value', 'label')}
          filterPlaceholderText="Filter by status"
          allowSearch={false}
          filterPlaceholderStyle="font-semibold"
          searchInputStyle="placeholder:text-primaryText"
        />
      )}
      {showTeamFilter && (
        <DropdownWithSearch
          searchFilterId="jobs-members-search-field"
          filterId={JobFilterIds.TEAM}
          data={getMerchantsResponse || []}
          searchText={assigneeSearchString}
          setSearchText={setAssigneeSearchString}
          selectedDraftArray={assigneesDraft}
          setSelectedDraftArray={setAssigneesDraft}
          selectedArray={selectedTeamMembers}
          setSelectedArray={handleTeamMembersChange}
          dataKey="firstName"
          secondaryDataKey="lastName"
          dataIdKey={'merchantId'}
          buttonPrimaryText="Team"
          buttonSecondaryText={getTextForFilter(
            getMerchantsResponse || [],
            selectedTeamMembers,
            'merchantId',
            'firstName'
          )}
          filterPlaceholderText="Filter by team"
          searchPlaceHolderText="Search team members"
          allowSearch={true}
          filterPlaceholderStyle="font-semibold"
          searchInputStyle="placeholder:text-primaryText"
        />
      )}
      <DropdownWithSearch
        searchFilterId="jobs-tags-search-field"
        filterId="jobs-tags-filter"
        data={tagFilterOptions}
        searchText={tagsSearchString}
        setSearchText={setTagsSearchString}
        selectedDraftArray={selectedTagsDraft}
        setSelectedDraftArray={setSelectedTagsDraft}
        selectedArray={selectedTags}
        setSelectedArray={handleTagsChange}
        dataKey="name"
        dataIdKey={'id'}
        buttonPrimaryText="Tags"
        buttonSecondaryText={getTextForFilter(tagFilterOptions, selectedTags, 'id', 'name')}
        filterPlaceholderText="Filter by tags"
        searchPlaceHolderText="Search tag names"
        allowSearch={true}
        filterPlaceholderStyle="font-semibold"
        searchInputStyle="placeholder:text-primaryText"
        showSeparatorAfterFirstItem={true}
      />
      <MoreFilters
        onFilterApply={handleApplyFilterFromMoreFilters}
        onFilterRemove={handleRemoveFilter}
        appliedFilters={appliedFilters}
        filterOptions={availableFilterOptions}
        getFilterData={getFilterData}
      />
    </>
  );
};

export default JobsFilter;
